import { QuestionCircleOutlined } from '@ant-design/icons'
import { isRegion, REGIONS } from '@root/src/libs/region'
import { Col, Grid, Row, Tooltip, Typography as Text } from 'antd'
import FlatList from 'flatlist-react'
import _ from 'lodash'
import moment from 'moment'
import React from 'react'
import { useTranslation } from 'react-i18next'

import SummaryRow from '@/components/SummaryRow'
import usePaymentMethods from '@/hooks/usePaymentMethods'
import { currencyWithCommas, numberWithCommas } from '@/libs/numberWithCommas'
import { supportsTips } from '@/libs/tipsVisibility'
import { useSelector } from '@/redux'
import colors from '@/style/colors'

import SummaryInfoColumn from './SummaryInfoColumn'

export default function SummaryInfo(props) {
  const { data } = props
  const { t, i18n } = useTranslation()
  const paymentMethods = usePaymentMethods()
  const activeMerchant = useSelector((state) => state.merchant.activeMerchant)
  const showTips = supportsTips(activeMerchant?.region ?? process.env.REACT_APP_REGION)
  const screens = Grid.useBreakpoint()

  const sectionsByType = React.useMemo(() => _.keyBy(data?.sections, 'type'), [data])

  const {
    sales: salesSection,
    paymentMethod: paymentMethodSection,
    paymentMethodTips: paymentMethodTipsSection,
    audit: auditSection,
    table: tableSection,
    merchantTakeaway: merchantTakeawaySection,
    dimOrderTakeaway: dimOrderTakeawaySection,
    dimOrderStoreDelivery: dimOrderStoreDeliverySection,
    dimOrderDineIn: dimOrderDineInSection,
    keeta: keetaSection,
    customPriceModifier: customPriceModifierSection,
    customItemPriceModifier: customItemPriceModifierSection,
    cash: cashSection,
    einvoice: einvoiceSection,
    tableGroupSales: tableGroupSalesSection,
  } = sectionsByType

  const keetaDataExists =
    keetaSection?.data?.sales?.count > 0 ||
    keetaSection?.data?.cancelled?.count > 0 ||
    keetaSection?.data?.totalSales > 0

  const renderTableSalesItem = (item, index) => {
    return (
      <SummaryRow
        key={String(index) + item.tableName}
        title={item.tableName}
        count={item.count}
        amount={currencyWithCommas(
          item.totalSales,
          activeMerchant?.setting?.currency,
          activeMerchant?.rounding?.minFractionDigits,
        )}
        renderTableSalesItem
      />
    )
  }

  const renderTotalItem = (item, { countStyle } = {}) => {
    return (
      <SummaryRow
        title={item.title}
        count={item.count}
        amount={item.amount}
        textStyle={item.textStyle}
        countStyle={{ ...countStyle }}
      />
    )
  }

  const setPaymentName = (type, name) => {
    return i18n.exists(name)
      ? t(name)
      : i18n.exists(`app.constant.payment.${type}`)
      ? t(`app.constant.payment.${type}`)
      : name || type
  }

  const paymentMethodMerchantData = [
    ...(paymentMethodSection?.data?.payments ?? []).map((payment) => ({
      title: setPaymentName(payment.type, payment.name),
      count: payment.count,
      amount: currencyWithCommas(
        payment.amount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
    })),
    {
      title: t('page.dailyClosing.total'),
      amount: currencyWithCommas(
        paymentMethodSection?.data?.total,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
      textStyle: { color: colors.textHighlight },
    },
  ]

  const tipsPaymentData = [
    ...(paymentMethodTipsSection?.data?.payments ?? []).map((payment) => ({
      title: paymentMethods[payment.type] ?? setPaymentName(payment.type, payment.name),
      count: payment.count,
      amount: currencyWithCommas(
        payment.amount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
    })),
    {
      title: t('page.dailyClosing.total'),
      amount: currencyWithCommas(
        paymentMethodTipsSection?.data?.total,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
      textStyle: { color: colors.textHighlight },
    },
  ]

  const auditData = [
    {
      title: t('page.dailyClosing.cancelledItem'),
      count: auditSection?.data?.cancelItem?.count,
      amount: currencyWithCommas(
        -1 * auditSection?.data?.cancelItem?.amount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
    },
    {
      title: t('page.dailyClosing.cancelledOrder'),
      count: auditSection?.data?.cancelOrder?.count,
      amount: currencyWithCommas(
        -1 * auditSection?.data?.cancelOrder?.amount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
    },
  ]

  const payInAmount = cashSection?.data?.cash?.find((c) => c.type === 'in')?.amount
  const payOutAmount = cashSection?.data?.cash?.find((c) => c.type === 'out')?.amount
  const payInAndOutTotal = cashSection?.data?.total

  const payInAndOutData = []

  if (payInAmount) {
    payInAndOutData.push({
      title: t('page.dailyClosing.payIn'),
      amount: currencyWithCommas(
        payInAmount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
    })
  }

  if (payOutAmount) {
    payInAndOutData.push({
      title: t('page.dailyClosing.payOut'),
      amount: `(${currencyWithCommas(
        payOutAmount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      )})`,
    })
  }

  if (payInAndOutTotal) {
    payInAndOutData.push({
      title: t('page.dailyClosing.total'),
      amount:
        payInAndOutTotal > 0
          ? `${currencyWithCommas(
              payInAndOutTotal,
              activeMerchant?.setting?.currency,
              activeMerchant?.rounding?.minFractionDigits,
            )}`
          : `(${currencyWithCommas(
              -1 * payInAndOutTotal,
              activeMerchant?.setting?.currency,
              activeMerchant?.rounding?.minFractionDigits,
            )})`,
      textStyle: { color: colors.textHighlight },
    })
  }

  const enableInvoice =
    activeMerchant?.setting?.enableInvoice && isRegion(activeMerchant?.region, REGIONS.TW)
  const einvoiceData = []
  const einvoiceTrackGroupRows = React.useMemo(() => {
    return (einvoiceSection?.data?.trackGroups ?? []).flatMap((trackGroup) =>
      (trackGroup?.groups ?? []).map((group) => {
        const voidOrRefundNos = group?.voidOrRefundNos ?? []
        const startNo = `${trackGroup?.track ?? ''}${group?.startNo ?? ''}`
        const endNo = `${trackGroup?.track ?? ''}${group?.endNo ?? ''}`
        return {
          range: (
            <>
              <span>{startNo}</span>
              {' ~ '}
              <span>{endNo}</span>
            </>
          ),
          issuedCount: group?.count ?? 0,
          issuedAmount: currencyWithCommas(
            group?.amount ?? 0,
            activeMerchant?.setting?.currency,
            activeMerchant?.rounding?.minFractionDigits,
          ),
          voidedCount: voidOrRefundNos.length,
          voidedAmount: currencyWithCommas(
            group?.voidAmount ?? 0,
            activeMerchant?.setting?.currency,
            activeMerchant?.rounding?.minFractionDigits,
          ),
          voidedNumbers:
            voidOrRefundNos.map((no) => `${trackGroup?.track ?? ''}${no}`).join(' ') || '-',
        }
      }),
    )
  }, [
    activeMerchant?.rounding?.minFractionDigits,
    activeMerchant?.setting?.currency,
    einvoiceSection,
  ])
  const noInvoiceRows = React.useMemo(() => {
    return (einvoiceSection?.data?.noInvoiceGroups ?? []).map((item) => ({
      terminalId: item?.terminalId ?? '',
      serial: item?.serial ?? '',
      paidAt: item?.paidAt ? moment.utc(item.paidAt).format('YYYY/MM/DD HH:mm:ss') : '',
      roundedTotal: currencyWithCommas(
        item?.roundedTotal ?? 0,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      ),
      checkoutStaff: item?.checkoutStaff ?? '',
    }))
  }, [
    activeMerchant?.rounding?.minFractionDigits,
    activeMerchant?.setting?.currency,
    einvoiceSection,
  ])

  const makeEinvoiceRow = (titleKey, count, amount, tooltip) => ({
    title: tooltip ? (
      <>
        {t(titleKey)}
        <Tooltip title={t(tooltip)}>
          <QuestionCircleOutlined style={{ paddingLeft: 10 }} />
        </Tooltip>
      </>
    ) : (
      t(titleKey)
    ),
    count,
    amount: currencyWithCommas(
      amount,
      activeMerchant?.setting?.currency,
      activeMerchant?.rounding?.minFractionDigits,
    ),
  })

  // enableInvoice=false: 有資料才顯示對應列
  if (
    enableInvoice ||
    einvoiceSection?.data?.issuedCount > 0 ||
    einvoiceSection?.data?.issuedAmount > 0 ||
    einvoiceSection?.data?.voidedCount > 0 ||
    einvoiceSection?.data?.voidedAmount > 0 ||
    einvoiceSection?.data?.allowanceCount > 0 ||
    einvoiceSection?.data?.allowanceAmount > 0
  ) {
    einvoiceData.push(
      makeEinvoiceRow(
        'page.dailyClosing.einvoiceIssued',
        einvoiceSection?.data?.issuedCount,
        einvoiceSection?.data?.issuedAmount,
        'page.dailyClosing.einvoiceTooltip',
      ),
    )
    einvoiceData.push(
      makeEinvoiceRow(
        'page.dailyClosing.einvoiceVoided',
        einvoiceSection?.data?.voidedCount,
        einvoiceSection?.data?.voidedAmount,
      ),
    )
    einvoiceData.push(
      makeEinvoiceRow(
        'page.dailyClosing.einvoiceAllowance',
        einvoiceSection?.data?.allowanceCount,
        einvoiceSection?.data?.allowanceAmount,
      ),
    )
  }

  const customOrderModifierData = [
    ...(customPriceModifierSection?.data?.modifiers ?? []).map((m) => ({
      title: m.name,
      count: m.count,
      amount: `(${currencyWithCommas(
        -1 * m.amount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      )})`,
    })),
    {
      title: t('page.dailyClosing.total'),
      amount: `(${currencyWithCommas(
        -1 * customPriceModifierSection?.data?.total,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      )})`,
      textStyle: { color: colors.textHighlight },
    },
  ]
  const customItemModifierData = [
    ...(customItemPriceModifierSection?.data?.modifiers ?? []).map((m) => ({
      title: m.name,
      count: m.count,
      amount: `(${currencyWithCommas(
        -1 * m.amount,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      )})`,
    })),
    {
      title: t('page.dailyClosing.total'),
      amount: `(${currencyWithCommas(
        -1 * customItemPriceModifierSection?.data?.total,
        activeMerchant?.setting?.currency,
        activeMerchant?.rounding?.minFractionDigits,
      )})`,
      textStyle: { color: colors.textHighlight },
    },
  ]

  return (
    <Row>
      <Col span={24}>
        <SummaryInfoColumn
          data={salesSection}
          title={
            <Text>
              {t('page.dailyClosing.merchantSales')}
              <Tooltip title={t('page.dailyClosing.tooltip')}>
                <QuestionCircleOutlined style={{ paddingLeft: 10 }} />
              </Tooltip>
            </Text>
          }
        />

        <SummaryRow
          title={t('page.dailyClosing.customOrderModifiers')}
          textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
        />
        <FlatList list={customOrderModifierData} renderItem={renderTotalItem} />

        <SummaryRow
          title={t('page.dailyClosing.customItemModifiers')}
          textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
        />
        <FlatList list={customItemModifierData} renderItem={renderTotalItem} />

        <SummaryRow
          title={t('page.dailyClosing.paymentMethodMerchant')}
          textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
        />
        <FlatList list={paymentMethodMerchantData} renderItem={renderTotalItem} />

        <SummaryInfoColumn
          data={dimOrderDineInSection}
          title={t('page.dailyClosing.dimorderDineIn')}
        />

        {showTips && (
          <>
            <SummaryRow
              title={t('page.dailyClosing.tipsPaymentMethod')}
              textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
            />
            <FlatList list={tipsPaymentData} renderItem={renderTotalItem} />
          </>
        )}

        <SummaryRow
          title={t('page.dailyClosing.audit')}
          textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
        />
        <FlatList list={auditData} renderItem={renderTotalItem} />

        {payInAndOutData.length > 0 && (
          <>
            <SummaryRow
              title={t('page.dailyClosing.payInAndOut')}
              textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
            />
            <FlatList list={payInAndOutData} renderItem={renderTotalItem} />
          </>
        )}

        <br />

        <SummaryInfoColumn data={tableSection} title={t('app.constant.deliveryType.table')} />

        <br />

        {/* 枱號群銷售單數與總額，沒有資料時不顯示 */}
        {tableGroupSalesSection?.data?.length > 0 && (
          <>
            <SummaryRow
              title={t('page.dailyClosing.tableGroupSales')}
              textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
            />
            <FlatList list={tableGroupSalesSection?.data} renderItem={renderTableSalesItem} />
          </>
        )}

        <br />

        <SummaryInfoColumn
          data={merchantTakeawaySection}
          title={t('page.dailyClosing.merchantTakeaway')}
        />

        <br />

        {keetaDataExists && (
          <>
            <SummaryInfoColumn data={keetaSection} title={t('page.dailyClosing.keeta')} />
            <br />
          </>
        )}

        <SummaryInfoColumn
          data={dimOrderTakeawaySection}
          title={t('page.dailyClosing.dimorderTakeaway')}
        />

        <br />

        <SummaryInfoColumn
          data={dimOrderStoreDeliverySection}
          title={t('page.dailyClosing.dimorderDelivery')}
        />

        <br />

        {(enableInvoice || einvoiceData.length > 0) && (
          <>
            {/* 電子發票概覽 */}
            <SummaryRow
              title={t('page.dailyClosing.einvoice')}
              textStyle={{ fontSize: 24, fontWeight: 'bold', paddingTop: 30 }}
            />
            {/* Section 1 */}
            {/* 電子發票狀態 */}
            <SummaryRow
              title={t('page.dailyClosing.einvoiceStatus')}
              count={t('page.dailyClosing.einvoiceQuantity')}
              amount={t('page.dailyClosing.einvoiceTotalAmount')}
              textStyle={{ fontSize: 16, color: '#999' }}
              countStyle={{ textAlign: 'left' }}
            />
            <FlatList
              list={einvoiceData}
              renderItem={(item) => renderTotalItem(item, { countStyle: { textAlign: 'left' } })}
            />

            {/* Section 2 */}
            {/* 電子發票開立統計 */}
            {einvoiceTrackGroupRows.length > 0 && (
              <>
                <br />
                <Row style={{ justifyContent: 'center' }}>
                  <Col xl={14} md={16} sm={18} xs={24}>
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 'bold',
                        paddingTop: 10,
                        textAlign: 'left',
                      }}
                    >
                      {t('page.dailyClosing.einvoiceTrackGroupTitle')}
                    </div>
                    <div style={{ marginTop: 20, overflowX: 'visible' }}>
                      <table
                        data-testid='einvoice-track-group-table'
                        style={{
                          width: screens.md ? '120%' : '100%',
                          borderCollapse: 'collapse',
                          tableLayout: 'fixed',
                        }}
                      >
                        <colgroup>
                          <col style={{ width: '30%' }} />
                          <col />
                          <col />
                          <col />
                          <col />
                        </colgroup>

                        <thead>
                          <tr>
                            <th style={styles.headerCell}>
                              {t('page.dailyClosing.einvoiceRange')}
                            </th>
                            <th style={styles.headerCell}>
                              {t('page.dailyClosing.einvoiceIssuedCount')}
                            </th>
                            <th style={{ ...styles.headerCell, ...styles.numberCell }}>
                              {t('page.dailyClosing.einvoiceIssuedAmount')}
                            </th>
                            <th style={styles.headerCell}>
                              {t('page.dailyClosing.einvoiceVoidedCount')}
                            </th>
                            <th style={{ ...styles.headerCell, ...styles.numberCell }}>
                              {t('page.dailyClosing.einvoiceVoidedAmount')}
                            </th>
                            <th style={styles.headerCell}>
                              {t('page.dailyClosing.einvoiceVoidedNumbers')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {einvoiceTrackGroupRows.map((row, index) => (
                            <tr key={`${row.range} -${String(index)} `}>
                              <td style={styles.bodyCell}>{row.range}</td>
                              <td style={styles.bodyCell}>{row.issuedCount}</td>
                              <td style={{ ...styles.bodyCell, ...styles.numberCell }}>
                                {row.issuedAmount}
                              </td>
                              <td style={styles.bodyCell}>{row.voidedCount}</td>
                              <td style={{ ...styles.bodyCell, ...styles.numberCell }}>
                                {row.voidedAmount}
                              </td>
                              <td style={styles.bodyCell}>{row.voidedNumbers}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
              </>
            )}
            {/* Section 3 */}
            {/* 未開立發票的帳單 */}
            {noInvoiceRows.length > 0 && (
              <>
                <br />
                <Row style={{ justifyContent: 'center' }}>
                  <Col xl={14} md={16} sm={18} xs={24}>
                    <div
                      style={{
                        fontSize: 24,
                        fontWeight: 'bold',
                        paddingTop: 10,
                        textAlign: 'left',
                      }}
                    >
                      {t('page.dailyClosing.noInvoiceTitle')}
                      <Tooltip title={t('page.dailyClosing.noInvoiceTooltip')}>
                        <QuestionCircleOutlined style={{ paddingLeft: 10 }} />
                      </Tooltip>
                    </div>
                    <div style={{ marginTop: 20, overflowX: 'visible' }}>
                      <table
                        data-testid='einvoice-no-invoice-table'
                        style={{
                          width: screens.md ? '120%' : '100%',
                          borderCollapse: 'collapse',
                          tableLayout: 'fixed',
                        }}
                      >
                        <colgroup>
                          <col />
                          <col />
                          <col style={{ width: '30%' }} />
                          <col />
                          <col />
                        </colgroup>
                        <thead>
                          <tr>
                            <th style={styles.headerCell}>{t('page.dailyClosing.terminalId')}</th>
                            <th style={styles.headerCell}>{t('page.dailyClosing.serial')}</th>
                            <th style={styles.headerCell}>{t('page.dailyClosing.paidAt')}</th>
                            <th style={{ ...styles.headerCell, ...styles.numberCell }}>
                              {t('page.dailyClosing.roundedTotal')}
                            </th>
                            <th style={styles.headerCell}>
                              {t('page.dailyClosing.checkoutStaff')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {noInvoiceRows.map((row, index) => (
                            <tr key={`${row.serial}-${String(index)}`}>
                              <td style={styles.bodyCell}>{row.terminalId}</td>
                              <td style={styles.bodyCell}>{row.serial}</td>
                              <td style={styles.bodyCell}>{row.paidAt}</td>
                              <td style={{ ...styles.bodyCell, ...styles.numberCell }}>
                                {row.roundedTotal}
                              </td>
                              <td style={styles.bodyCell}>{row.checkoutStaff}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </Col>
                </Row>
              </>
            )}
          </>
        )}
      </Col>
    </Row>
  )
}

const styles = {
  headerCell: {
    border: 'none',
    color: colors.textSecondary,
    fontSize: 14,
    fontWeight: 600,
    textAlign: 'left',
    verticalAlign: 'top',
    paddingBottom: 12,
  },
  bodyCell: {
    border: 'none',
    fontSize: 16,
    paddingBottom: 4,
    // padding: '12px 8px',
    textAlign: 'left',
    verticalAlign: 'top',
    wordBreak: 'break-word',
  },
  numberCell: {
    textAlign: 'right',
    paddingRight: 16,
  },
}
